// ColumnFilterDropdown.tsx - Lightweight column header filter and sort
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import { ChevronDown, SortAsc, SortDesc, Filter, X } from 'lucide-react';
import { operatorOptions } from '../../views/leads/LeadFilterUtils';
import './ColumnFilterDropdown.css';

export interface ColumnFilterConfig {
  operator?: any;
  value?: any;
  endValue?: any; // For "between" operator
}

export interface ColumnFilterDropdownProps {
  colId: string;
  headerName: string;
  fieldType?: string; // text, number, date, selection, etc.
  fieldOptions?: Array<{ label: string; value: string }>; // For selection/multi-selection fields
  currentFilter?: ColumnFilterConfig;
  currentSort?: 'asc' | 'desc' | null;
  onFilterApply: (colId: string, filter: ColumnFilterConfig | null) => void;
  onSort?: (colId: string, direction: 'asc' | 'desc') => void;
  allowSort?: boolean;
  allowFilter?: boolean;
}

const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({
  colId,
  headerName,
  fieldType = 'text',
  fieldOptions = [],
  currentFilter,
  currentSort,
  onFilterApply,
  onSort,
  allowSort = true,
  allowFilter = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localOperator, setLocalOperator] = useState<any>(currentFilter?.operator);
  const [localValue, setLocalValue] = useState<any>(currentFilter?.value || '');
  const [localEndValue, setLocalEndValue] = useState<any>(currentFilter?.endValue || '');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Sync with props when they change
  useEffect(() => {
    setLocalOperator(currentFilter?.operator);
    setLocalValue(currentFilter?.value || '');
    setLocalEndValue(currentFilter?.endValue || '');
  }, [currentFilter]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      setDropdownPosition({
        top: rect.bottom + scrollY + 4, // 4px gap below button
        left: rect.left + scrollX,
        width: Math.max(rect.width, 280), // Min width 280px
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  // Get operator options based on field type
  const getOperatorOptionsForField = (): Array<{ label: string; value: string }> => {
    switch (fieldType) {
      case 'text':
      case 'email':
      case 'phone':
        return [
          { label: 'is', value: 'eq' },
          { label: 'contains', value: 'contain' },
          { label: 'does not contain', value: 'doesNotContain' },
          { label: 'is not', value: 'ne' },
        ];

      case 'number':
      case 'amount':
        return [
          { label: 'equals', value: 'eq' },
          { label: 'greater than', value: 'gt' },
          { label: 'less than', value: 'lt' },
          { label: 'between', value: 'between' },
        ];

      case 'date':
        return [
          { label: 'is', value: 'eq' },
          { label: 'is before', value: 'lt' },
          { label: 'is after', value: 'gt' },
          { label: 'between', value: 'between' },
        ];

      case 'selection':
        return [
          { label: 'is', value: 'eq' },
          { label: 'is not', value: 'ne' },
        ];

      case 'multi-selection':
        return [
          { label: 'contains', value: 'contain' },
          { label: 'does not contain', value: 'doesNotContain' },
        ];

      default:
        return operatorOptions;
    }
  };

  const handleApplyFilter = () => {
    if (!localOperator || !localValue) {
      // Clear filter if incomplete
      onFilterApply(colId, null);
    } else {
      onFilterApply(colId, {
        operator: localOperator,
        value: localValue,
        ...(localOperator.value === 'between' && localEndValue ? { endValue: localEndValue } : {})
      });
    }
    setIsOpen(false);
  };

  const handleClearFilter = () => {
    setLocalOperator(undefined);
    setLocalValue('');
    setLocalEndValue('');
    onFilterApply(colId, null);
  };

  const handleSort = (direction: 'asc' | 'desc') => {
    if (onSort) {
      onSort(colId, direction);
    }
    setIsOpen(false);
  };

  const renderValueInput = () => {
    // For selection fields with predefined options
    if ((fieldType === 'selection' || fieldType === 'multi-selection') && fieldOptions.length > 0) {
      return (
        <Select
          value={localValue ? { label: localValue, value: localValue } : null}
          options={fieldOptions}
          onChange={(option: any) => setLocalValue(option?.value || '')}
          placeholder="Select value..."
          isClearable
          className="value-select"
          classNamePrefix="select"
        />
      );
    }

    // For date fields
    if (fieldType === 'date') {
      return (
        <div className="date-inputs">
          <DatePicker
            format="DD MMM YYYY"
            value={localValue ? moment(localValue) : null}
            onChange={(date) => setLocalValue(date ? moment(date).format() : '')}
            placeholder="Select date"
            style={{ width: '100%' }}
          />
          {localOperator?.value === 'between' && (
            <>
              <span className="range-separator">to</span>
              <DatePicker
                format="DD MMM YYYY"
                value={localEndValue ? moment(localEndValue) : null}
                onChange={(date) => setLocalEndValue(date ? moment(date).format() : '')}
                placeholder="End date"
                style={{ width: '100%' }}
              />
            </>
          )}
        </div>
      );
    }

    // For number/amount fields
    if (fieldType === 'number' || fieldType === 'amount') {
      return (
        <div className="number-inputs">
          <input
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={localOperator?.value === 'between' ? 'Start value' : 'Enter value'}
            className="value-input"
          />
          {localOperator?.value === 'between' && (
            <>
              <span className="range-separator">to</span>
              <input
                type="number"
                value={localEndValue}
                onChange={(e) => setLocalEndValue(e.target.value)}
                placeholder="End value"
                className="value-input"
              />
            </>
          )}
        </div>
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder="Enter value"
        className="value-input"
      />
    );
  };

  const hasActiveFilter = currentFilter && currentFilter.operator && currentFilter.value;
  const hasActiveSort = currentSort !== null;

  // Render dropdown menu
  const renderDropdownMenu = () => {
    if (!isOpen || !dropdownPosition) return null;

    const menuContent = (
      <div
        ref={dropdownRef}
        className="column-filter-dropdown-menu"
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          minWidth: `${dropdownPosition.width}px`,
        }}
      >
          {/* Sort Section */}
          {allowSort && onSort && (
            <div className="dropdown-section">
              <div className="section-title">Sort</div>
              <button
                className={`menu-item ${currentSort === 'asc' ? 'active' : ''}`}
                onClick={() => handleSort('asc')}
              >
                <SortAsc size={14} />
                <span>Sort A → Z</span>
              </button>
              <button
                className={`menu-item ${currentSort === 'desc' ? 'active' : ''}`}
                onClick={() => handleSort('desc')}
              >
                <SortDesc size={14} />
                <span>Sort Z → A</span>
              </button>
            </div>
          )}

          {/* Filter Section */}
          {allowFilter && (
            <>
              <div className="dropdown-divider" />
              <div className="dropdown-section">
                <div className="section-title">
                  <Filter size={14} />
                  <span>Filter</span>
                </div>

                {/* Operator Selection */}
                <div className="filter-field">
                  <label>Condition</label>
                  <Select
                    value={localOperator}
                    options={getOperatorOptionsForField()}
                    onChange={setLocalOperator}
                    placeholder="Select condition..."
                    isClearable
                    className="operator-select"
                    classNamePrefix="select"
                  />
                </div>

                {/* Value Input */}
                {localOperator && (
                  <div className="filter-field">
                    <label>Value</label>
                    {renderValueInput()}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="filter-actions">
                  <button
                    className="btn-clear"
                    onClick={handleClearFilter}
                    disabled={!hasActiveFilter}
                  >
                    <X size={14} />
                    Clear
                  </button>
                  <button
                    className="btn-apply"
                    onClick={handleApplyFilter}
                    disabled={!localOperator || !localValue}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </>
          )}
      </div>
    );

    // Use portal to render dropdown at body level (avoids overflow issues)
    return ReactDOM.createPortal(menuContent, document.body);
  };

  return (
    <div className="column-filter-dropdown-container">
      <button
        ref={buttonRef}
        className={`column-header-button ${hasActiveFilter || hasActiveSort ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        title={`Filter and sort ${headerName}`}
      >
        <span className="header-text">{headerName}</span>
        <ChevronDown
          size={14}
          className={`dropdown-icon ${isOpen ? 'open' : ''}`}
        />
        {(hasActiveFilter || hasActiveSort) && (
          <span className="active-indicator" />
        )}
      </button>

      {renderDropdownMenu()}
    </div>
  );
};

export default ColumnFilterDropdown;
